import { Icon } from '@iconify/react'
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import type { CampaignStatus } from '@/lib/campaigns'
import styles from './CampaignStatusMenu.module.css'

const STATUS_OPTIONS: { value: CampaignStatus; label: string }[] = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'active', label: 'Ativa' },
  { value: 'finished', label: 'Finalizada' },
  { value: 'cancelled', label: 'Cancelada' },
]

type CampaignStatusMenuProps = {
  control: { value: CampaignStatus; onChange: (status: CampaignStatus) => void } | null
  disabled: boolean
}

export function CampaignStatusMenu({ control, disabled }: CampaignStatusMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const labelId = useId()
  const menuId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const selectedIndex = STATUS_OPTIONS.findIndex(
    (option) => option.value === control?.value
  )
  const selectedOption = STATUS_OPTIONS[selectedIndex]
  const isDisabled = disabled || !control

  useEffect(() => {
    if (!isOpen) return

    optionRefs.current[Math.max(selectedIndex, 0)]?.focus()

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick)
  }, [isOpen, selectedIndex])

  const selectOption = (status: CampaignStatus) => {
    control?.onChange(status)
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = optionRefs.current.findIndex(
      (option) => option === document.activeElement
    )
    let nextIndex = currentIndex

    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % STATUS_OPTIONS.length
    else if (event.key === 'ArrowUp')
      nextIndex = (currentIndex - 1 + STATUS_OPTIONS.length) % STATUS_OPTIONS.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = STATUS_OPTIONS.length - 1
    else if (event.key === 'Escape') {
      event.preventDefault()
      setIsOpen(false)
      triggerRef.current?.focus()
      return
    } else if (event.key === 'Tab') {
      setIsOpen(false)
      return
    } else return

    event.preventDefault()
    optionRefs.current[nextIndex]?.focus()
  }

  return (
    <div className={styles.field} ref={rootRef}>
      <span className={styles.label} id={labelId}>
        Status da Campanha
      </span>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-labelledby={labelId}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        disabled={isDisabled}
        title="Altere o status e clique em Salvar campanha para aplicar."
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            setIsOpen(true)
          }
        }}
      >
        {selectedOption && (
          <span className={styles.statusDot} data-status={selectedOption.value} />
        )}
        <span className={styles.triggerText}>
          {selectedOption?.label ?? 'Carregando...'}
        </span>
        <Icon icon="mdi:chevron-down" className={styles.chevron} aria-hidden />
      </button>

      {isOpen && !isDisabled && (
        <div
          id={menuId}
          className={styles.menu}
          role="menu"
          aria-labelledby={labelId}
          onKeyDown={handleMenuKeyDown}
        >
          {STATUS_OPTIONS.map((option, index) => (
            <button
              key={option.value}
              ref={(element) => {
                optionRefs.current[index] = element
              }}
              type="button"
              role="menuitemradio"
              aria-checked={control.value === option.value}
              className={styles.option}
              data-selected={control.value === option.value}
              onClick={() => selectOption(option.value)}
            >
              <span className={styles.statusDot} data-status={option.value} />
              <span>{option.label}</span>
              {control.value === option.value && (
                <Icon icon="mdi:check" className={styles.check} aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
